import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { 
  Zap, 
  Battery, 
  Power, 
  Lightbulb, 
  Settings, 
  ArrowRight,
  CheckCircle,
  Star,
  Globe,
  Shield,
  Gauge,
  Cable,
  Building
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { fadeInUp, fadeInLeft, fadeInRight, staggerContainer } from '../../utils/cn';

const PowerDivision = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

 
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const products = [
    {
      icon: Power,
      title: 'Power Distribution Systems',
      description: 'Comprehensive power distribution and control solutions',
      features: ['Switchgear Systems', 'Distribution Panels', 'Motor Control Centers', 'Power Factor Correction'],
      image: '/divisions/Power - Solar/electrical-substation-distribution-power-plant-wit-2024-07-25-01-00-57-utc.jpg'
    },
    {
      icon: Battery,
      title: 'UPS & Battery Systems',
      description: 'Uninterruptible power supply and backup power solutions',
      features: ['Online UPS Systems', 'Battery Banks', 'DC Power Systems', 'Energy Storage Solutions'],
      image: '/divisions/Power - Solar/nuclear-power-station-panorama-at-night-2023-11-27-04-54-50-utc.jpg'
    },
    {
      icon: Zap,
      title: 'Electrical Protection',
      description: 'Advanced electrical protection and safety systems',
      features: ['Circuit Breakers', 'Protective Relays', 'Surge Protection', 'Arc Flash Protection'],
      image: '/divisions/Power - Solar/high-voltage-electricity-poles-tower-power-stati-2023-11-27-04-56-35-utc.jpg'
    },
    {
      icon: Lightbulb,
      title: 'Lighting Solutions',
      description: 'Energy-efficient lighting systems and controls',
      features: ['LED Lighting', 'Smart Controls', 'Emergency Lighting', 'Industrial Lighting'],
      image: '/divisions/Power - Solar/solar-panels-in-city-2023-11-27-05-26-26-utc.jpg'
    },
    {
      icon: Gauge,
      title: 'Monitoring & Control',
      description: 'Power monitoring and automation systems',
      features: ['Energy Meters', 'SCADA Systems', 'Power Quality Analyzers', 'Remote Monitoring'],
      image: '/divisions/Power - Solar/inspector-examination-of-photovoltaic-modules-usin-2024-04-15-18-17-19-utc.jpg'
    },
    {
      icon: Cable,
      title: 'Cables & Accessories',
      description: 'High-quality cables and electrical accessories',
      features: ['Power Cables', 'Control Cables', 'Cable Accessories', 'Termination Kits'],
      image: '/divisions/Power - Solar/engineer-service-check-installation-solar-cell-on-2024-07-27-02-28-21-utc.jpg'
    }
  ];

  const solutions = [
    {
      title: 'Industrial Power Systems',
      description: 'Robust power solutions for industrial facilities and manufacturing plants',
      applications: ['Manufacturing Plants', 'Process Industries', 'Heavy Machinery', 'Production Lines'],
      image: '/divisions/Power - Solar/power-plant-near-coast-2023-11-27-05-22-28-utc.jpg'
    },
    {
      title: 'Commercial Buildings',
      description: 'Efficient power distribution for commercial and office buildings',
      applications: ['Office Buildings', 'Shopping Centers', 'Hotels & Resorts', 'Educational Facilities'],
      image: '/divisions/Power - Solar/solar-panels-in-city-2023-11-27-05-37-18-utc.jpg'
    },
    {
      title: 'Infrastructure Projects',
      description: 'Power systems for critical infrastructure and utilities',
      applications: ['Airports', 'Hospitals', 'Data Centers', 'Transportation Hubs'],
      image: '/divisions/Power - Solar/pole-mounted-transformers-ready-to-use-as-electrom-2023-11-27-05-10-47-utc.jpg'
    },
    {
      title: 'Renewable Energy',
      description: 'Integration solutions for renewable energy systems',
      applications: ['Solar Power Plants', 'Wind Farms', 'Energy Storage', 'Grid Integration'],
      image: '/divisions/Power - Solar/solar-power-energy-plant-2023-11-27-05-13-48-utc.jpg'
    }
  ];

  const brands = [
    { name: 'ABB', logo: '/Suppliers logo/ABB.png' },
    { name: 'Schneider Electric', logo: '/Suppliers logo/schneider-logo.png' },
    { name: 'Siemens', logo: '/Suppliers logo/siemens.png' },
    { name: 'Eaton', logo: '/Suppliers logo/Eaton.jpg' },
    { name: 'Honeywell', logo: '/Suppliers logo/Honeywell-Logo.png' },
    { name: 'GE', logo: '/Suppliers logo/GE.png' },
    { name: 'Emerson', logo: '/Suppliers logo/emerson.png' },
    { name: 'Phoenix Contact', logo: '/Suppliers logo/phoenix-contact-logo.png' },
    { name: 'Fluke', logo: '/Suppliers logo/Fluke.png' },
    { name: 'Moxa', logo: '/Suppliers logo/Moxa.png' },
    { name: 'Mean Well', logo: '/Suppliers logo/Mean-Well.png' },
    { name: 'Legrand', logo: '/Suppliers logo/legrand-logo.png' },
    { name: 'Mitsubishi Electric', logo: '/Suppliers logo/mitsubishi-electric.png' }
  ];

  const services = [
    {
      title: 'System Design',
      description: 'Custom electrical system design and engineering'
    },
    {
      title: 'Installation',
      description: 'Professional installation and commissioning'
    },
    {
      title: 'Testing',
      description: 'Comprehensive testing and validation services'
    },
    {
      title: 'Maintenance',
      description: 'Preventive and corrective maintenance programs'
    }
  ];

  const capabilities = [
    {
      title: 'Power Quality Analysis',
      description: 'Comprehensive power quality assessment and improvement solutions',
      icon: Gauge
    },
    {
      title: 'Energy Efficiency',
      description: 'Energy audit and optimization services to reduce operational costs',
      icon: Lightbulb
    },
    {
      title: 'Safety Compliance',
      description: 'Ensuring compliance with international electrical safety standards',
      icon: Shield
    },
    {
      title: 'Smart Grid Solutions',
      description: 'Advanced grid automation and smart metering technologies',
      icon: Settings
    }
  ];

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section 
        className="section-padding text-white relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.6)), url('/divisions/Power - Solar/electrical-substation-distribution-power-plant-wit-2024-07-25-01-00-57-utc.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Geometric Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `
                repeating-linear-gradient(
                  45deg,
                  transparent,
                  transparent 2px,
                  rgba(255, 255, 255, 0.1) 2px,
                  rgba(255, 255, 255, 0.1) 4px
                )
              `,
              backgroundSize: '20px 20px'
            }}
          />
        </div>

        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [-20, -100],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: Math.random() * 3 + 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        <div className="container-max relative z-10">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            {...fadeInUp}
          >
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                {t('divisions.power.title')}
              </h1>
              <p className="text-xl md:text-xl mb-8 leading-relaxed">
                We offer a comprehensive range of commercial power generation systems 
                and products designed to deliver reliable, efficient, and sustainable energy 
                solutions. From system design and installation to ongoing maintenance, 
                we provide end-to-end solutions tailored to meet your energy needs and 
                sustainability goals. Our products include solar panels, inverters, and energy 
                storage solutions, all engineered to meet the demands of your business. 
                Whether for backup power, continuous energy supply, or renewable 
                integration, our high-performance products ensure operational continuity and 
                efficiency. With a focus on sustainability, efficiency, and dependability, we are 
                your trusted partner in powering your business with cutting-edge technology 
                and unmatched expertise.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" variant="secondary" className="bg-burnt-orange hover:bg-metallic-gold transform hover:scale-105 transition-all duration-300">
                    Get Quote
                    <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                  </Button>
                </Link>
                <Link to="/powerdivisioncompany">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-charcoal backdrop-blur-sm">
                    Our Partner Companies
                  </Button>
                </Link>
              </div>
            </div>
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="relative mb-6">
                  <img 
                    src="/divisions/Power - Solar/solar-panels-producing-green-renewable-energy-sol-2023-11-27-05-31-29-utc.jpg"
                    alt="Solar Energy Solutions"
                    className="w-full h-48 object-cover rounded-xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-xl" />
                  <Zap className="absolute top-4 right-4 w-12 h-12 text-accent" />
                </div>
                <div className="text-center">
                  <h3 className="text-2xl font-semibold mb-4">Reliable Power Solutions</h3>
                  <p className="text-lg opacity-90">
                    Providing Cutting-Edge 
                    Commercial Power Generation 
                    Systems & Products
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Infrastructure Gallery Section */}
      <section className="section-padding bg-white text-black">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Power & Solar Infrastructure
            </h2>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              Explore our comprehensive power generation and distribution infrastructure projects.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              {
                src: "/divisions/Power - Solar/arrays-of-blue-solar-panels-2023-11-27-05-06-18-utc.jpg",
                title: "Solar Panel Arrays",
                description: "Large-scale photovoltaic installations"
              },
              {
                src: "/divisions/Power - Solar/high-voltage-electricity-poles-tower-power-stati-2023-11-27-04-56-35-utc.jpg",
                title: "High Voltage Transmission",
                description: "Power transmission infrastructure"
              },
              {
                src: "/divisions/Power - Solar/engineer-in-charge-of-solar-panel-installation-the-2023-11-27-04-52-54-utc.jpg",
                title: "Professional Installation",
                description: "Expert solar system installation"
              },
              {
                src: "/divisions/Power - Solar/power-plant-near-coast-2023-11-27-05-22-28-utc.jpg",
                title: "Power Generation",
                description: "Coastal power generation facilities"
              },
              {
                src: "/divisions/Power - Solar/solar-power-energy-plant-2023-11-27-05-13-48-utc.jpg",
                title: "Solar Power Plants",
                description: "Utility-scale solar installations"
              },
              {
                src: "/divisions/Power - Solar/pole-mounted-transformers-ready-to-use-as-electrom-2023-11-27-05-10-47-utc.jpg",
                title: "Distribution Equipment",
                description: "Transformer and distribution systems"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="group relative overflow-hidden rounded-xl bg-white/5 backdrop-blur-sm border border-white/10"
                variants={fadeInUp}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={item.src}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-lg font-semibold text-white mb-1">{item.title}</h3>
                    <p className="text-sm text-white/80">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Products Section */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Power Products & Systems
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              Comprehensive range of electrical products and systems for reliable power distribution.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {products.map((product, index) => {
              const Icon = product.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                >
                  <Card className="h-full hover:scale-105 transition-transform duration-300 overflow-hidden">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={product.image}
                        alt={product.title}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute top-4 right-4 w-12 h-12 bg-gradient-to-br from-burnt-orange to-metallic-gold rounded-full flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-lg">{product.title}</CardTitle>
                      <CardDescription>{product.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {product.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center text-sm text-slate">
                            <CheckCircle className="w-4 h-4 text-burnt-orange mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="section-padding bg-soft-gray">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Our Capabilities
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              Advanced capabilities in power system design, analysis, and optimization.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {capabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-charcoal to-burnt-orange rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-charcoal mb-3">{capability.title}</h3>
                  <p className="text-slate">{capability.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Industry Solutions
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              Tailored power solutions for diverse industry applications and requirements.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
              >
                <Card className="h-full hover:scale-105 transition-transform duration-300 overflow-hidden">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={solution.image}
                      alt={solution.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute top-4 right-4 w-12 h-12 bg-gradient-to-br from-burnt-orange to-metallic-gold rounded-full flex items-center justify-center">
                      <Building className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{solution.title}</CardTitle>
                    <CardDescription>{solution.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm text-slate">Applications:</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {solution.applications.map((app, appIndex) => (
                          <div key={appIndex} className="flex items-center text-sm text-slate">
                            <CheckCircle className="w-4 h-4 text-burnt-orange mr-2 flex-shrink-0" />
                            {app}
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      


      {/* Brands Section */}
      <section className="section-padding bg-soft-gray">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Brands Global Technology Distributes
            </h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              We represent world-renowned manufacturers of electrical and power equipment.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {brands.map((brand, index) => (
              <motion.div
                key={index}
                className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                variants={fadeInUp}
              >
                <img
                  src={brand.logo}
                  alt={brand.name}
                  className="w-16 h-16 object-contain mx-auto mb-3"
                  onError={(e) => {
                    e.target.src = '/placeholder-logo.svg';
                  }}
                />
                <h3 className="font-semibold text-charcoal mb-1">{brand.name}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* La Marche Company Section */}


      {/* Services Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="/divisions/Power - Solar/pink-night-sky-electrical-electricity-cable-commun-2023-11-27-04-50-53-utc.jpg"
            alt="Electrical Infrastructure"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/80" />
          {/* Geometric Pattern Overlay */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-full">
              <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                <defs>
                  <pattern id="grid-pattern-services" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
                    <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#grid-pattern-services)" />
              </svg>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6 text-white">Professional Services</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Complete lifecycle support for your electrical and power systems.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="text-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/20 transition-all duration-300 hover:scale-105"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-burnt-orange to-metallic-gold rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">{service.title}</h3>
                <p className="text-gray-300">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16 relative overflow-hidden">        
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-5 left-10 w-20 h-20 bg-teal-400/20 rounded-full blur-xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-10 right-10 w-24 h-24 bg-accent/20 rounded-full blur-xl"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.5, 0.2]
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
          />
        </div>
        
        <div className="container-max relative z-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center min-h-[400px]">
            {/* Left Side - Polygon Design Pattern */}
            <div className="relative h-full lg:col-span-2">
              {/* Polygon Shape with Hero-style Design */}
              <div 
                className="absolute inset-0 bg-gradient-to-br from-teal-400 via-teal-500 to-teal-600 z-5 w-[1000px] h-full"
                style={{
                  clipPath: 'polygon(0 0, 100% 0, 70% 100%, 0% 100%)'
                }}
              >
                {/* Clean Geometric Pattern */}
                <div 
                  className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage: `
                      repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 20px,
                        rgba(255,255,255,0.3) 20px,
                        rgba(255,255,255,0.3) 22px,
                        transparent 22px,
                        transparent 40px,
                        rgba(255,255,255,0.2) 40px,
                        rgba(255,255,255,0.2) 42px
                      ),
                      repeating-linear-gradient(
                        -45deg,
                        transparent,
                        transparent 30px,
                        rgba(255,255,255,0.2) 30px,
                        rgba(255,255,255,0.2) 32px,
                        transparent 32px,
                        transparent 60px,
                        rgba(255,255,255,0.1) 60px,
                        rgba(255,255,255,0.1) 62px
                      )
                    `,
                    backgroundSize: '80px 80px, 100px 100px'
                  }}
                />
                {/* Additional Geometric Lines */}
                <div 
                  className="absolute inset-0 opacity-8"
                  style={{
                    backgroundImage: `
                      repeating-linear-gradient(
                        0deg,
                        transparent,
                        transparent 50px,
                        rgba(255,255,255,0.15) 50px,
                        rgba(255,255,255,0.15) 51px
                      ),
                      repeating-linear-gradient(
                        90deg,
                        transparent,
                        transparent 50px,
                        rgba(255,255,255,0.15) 50px,
                        rgba(255,255,255,0.15) 51px
                      )
                    `
                  }}
                />
              </div>
              
              {/* Content over polygon */}
              <motion.div
                className="relative z-20 text-white p-6 lg:p-8 h-full flex flex-col justify-center"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >                
                <motion.h2 
                  className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  Power Your Success
                </motion.h2>
                
                <motion.p 
                  className="text-base lg:text-lg mb-6 text-gray-100 leading-relaxed font-bold"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  Contact our power systems experts to discuss your electrical infrastructure needs and requirements.
                  
                </motion.p>
                
                <motion.div 
                  className="flex flex-col sm:flex-row gap-3"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <Link to="/contact">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button size="lg" className="bg-black text-white hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl">
                        Get a Free Quote
                        <motion.div
                          animate={{ x: [0, 5, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} />
                        </motion.div>
                      </Button>
                    </motion.div>
                  </Link>
                  <Link to="/industries">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button variant="outline" size="lg" className="border-white/50 text-white hover:bg-white/10 hover:border-white backdrop-blur-sm transition-all duration-300">
                        view industries
                      </Button>
                    </motion.div>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PowerDivision;